import { Menu } from '@base-ui/react/menu'
import { twMerge } from 'tailwind-merge'
import type { PartProps } from '../../../lib/part-props'

/**
 * Props interface for MenuRadioGroup component.
 * Extends base-ui's Menu.RadioGroup.Props for radio group functionality.
 */
interface MenuRadioGroupProps extends PartProps<Menu.RadioGroup.Props> {
  className?: string
}

/**
 * MenuRadioGroup - Groups related radio items in a menu.
 *
 * This component manages the state of a group of radio items, ensuring
 * that only one item can be selected at a time. The selected value is
 * managed through the `value` and `onValueChange` props.
 *
 * Features:
 * - Controlled or uncontrolled state management
 * - Keyboard navigation within the group
 * - Proper ARIA attributes for accessibility
 * - Automatic radio button behavior (only one selected)
 *
 * @example
 * ```tsx
 * const [theme, setTheme] = useState('light')
 *
 * <Menu.RadioGroup value={theme} onValueChange={setTheme}>
 *   <Menu.RadioItem value="light">
 *     <Menu.RadioItemIndicator>
 *       <DotIcon />
 *     </Menu.RadioItemIndicator>
 *     <span>Light</span>
 *   </Menu.RadioItem>
 *   <Menu.RadioItem value="dark">
 *     <Menu.RadioItemIndicator>
 *       <DotIcon />
 *     </Menu.RadioItemIndicator>
 *     <span>Dark</span>
 *   </Menu.RadioItem>
 * </Menu.RadioGroup>
 * ```
 */
function MenuRadioGroup({ className, children, ...props }: MenuRadioGroupProps) {
  return (
    <Menu.RadioGroup className={twMerge(className)} {...props}>
      {children}
    </Menu.RadioGroup>
  )
}

export { MenuRadioGroup }
